import {
  Button,
  ErrorBoundary,
  FlexBox,
  Icon,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import type {
  ContextControl,
  Controls,
  FilterControl,
  InstrumentPanel,
  PeriodControl,
  SettingsControl,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import { memo, ReactNode, useCallback } from 'react';
import { useToggle, useUpdateEffect } from 'react-use';

import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { analyticsActions } from '../../analytics.actions';
import { useAnalyticsState } from '../panels/utils/useAnalyticsState/useAnalyticsState';

import styles from './analyticsControls.module.scss';
import { AnalyticsContext } from './components/context/analyticsContext';
import { AnalyticsFilters } from './components/filters/analyticsFilters';
import { AnalyticsPeriod } from './components/period/analyticsPeriod';

export type AnalyticsControlsProps = {
  /** The parent instrument panel, if relevant. */
  panel?: InstrumentPanel;
  /** Whether the filters are initially collapsed. */
  initiallyCollapsedFilters?: boolean;
  /**
   * A map of controls that should be disabled, where the key is the control
   * type and the value a text or element with the reason why it is disabled.
   */
  disabledControls?: Map<keyof Controls, ReactNode>;
  /** Callback for when one or more controls are changed. */
  onChangeControls?: (controls: Partial<Controls>) => void;
};

export const AnalyticsControls = memo(function AnalyticsControls({
  panel,
  initiallyCollapsedFilters = false,
  disabledControls,
  onChangeControls,
}: AnalyticsControlsProps): JSX.Element {
  const { dashboard, context, period, filters, settings } =
    useAnalyticsState(panel);
  const dispatch = useAppDispatch();

  const [hasCollapsedFilters, toggleHasCollapseFilters] = useToggle(
    initiallyCollapsedFilters,
  );

  useUpdateEffect(() => {
    toggleHasCollapseFilters(initiallyCollapsedFilters);
  }, [initiallyCollapsedFilters]);

  const handleChangeContext = useCallback(
    (context: ContextControl) => {
      if (panel) {
        onChangeControls?.({ context });
      } else {
        dispatch(analyticsActions.setContext({ context }));
      }

      ampli.analyticsContextUpdated({
        dashboard: dashboard.title,
        panel: panel?.title,
      });
    },
    [dashboard.title, dispatch, onChangeControls, panel],
  );

  const handleChangePeriod = useCallback(
    (period: PeriodControl) => {
      if (panel) {
        onChangeControls?.({ period });
      } else {
        dispatch(analyticsActions.setPeriod({ period }));
      }

      ampli.analyticsPeriodUpdated({
        dashboard: dashboard.title,
        panel: panel?.title,
        value:
          period.values.length === 1
            ? period.values[0].value
            : 'dateWithDefaultTime',
        timezone: settings.timezone,
      });
    },
    [dashboard.title, dispatch, onChangeControls, panel, settings.timezone],
  );

  const handleChangeFilters = useCallback(
    (filters: FilterControl[]) => {
      if (panel) {
        onChangeControls?.({ filters });
      } else {
        dispatch(analyticsActions.setFilters({ filters }));
      }
    },
    [dispatch, onChangeControls, panel],
  );

  const handleChangeSettings = useCallback(
    (newSettings: SettingsControl, newPeriod?: PeriodControl) => {
      if (panel) {
        onChangeControls?.({
          settings: { ...settings, ...newSettings },
          ...(newPeriod && { period: newPeriod }),
        });
      } else {
        dispatch(
          analyticsActions.setSettings({
            settings: { ...settings, ...newSettings },
            ...(newPeriod && { period: newPeriod }),
          }),
        );
      }

      if (newSettings.timezone !== settings.timezone) {
        ampli.analyticsTimezoneUpdated({
          dashboard: dashboard.title,
          panel: panel?.title,
          timezone: newSettings.timezone,
        });
      }

      if (newSettings.schedule_id !== settings.schedule_id) {
        if (newSettings.schedule_id) {
          ampli.analyticsBusinessHoursScheduleUpdated({
            dashboard: dashboard.title,
            panel: panel?.title,
            timezone: newSettings.timezone,
          });
        } else {
          ampli.analyticsBusinessHoursScheduleRemoved({
            dashboard: dashboard.title,
            panel: panel?.title,
            timezone: settings.timezone,
          });
        }
      }
    },
    [dashboard.title, dispatch, onChangeControls, panel, settings],
  );

  return (
    <ErrorBoundary
      fallback={
        <Text.Paragraph color={colors.red[500]}>
          Filters are currently unavailable due to an unexpected error. Our team
          has been notified and is looking into the issue.{' '}
          <a href="#" onClick={() => window.location.reload()}>
            Try reloading.
          </a>
        </Text.Paragraph>
      }
    >
      <div className={styles.controls}>
        <FlexBox gap="300" alignItems="center">
          {dashboard.context && (
            <AnalyticsContext
              panel={panel}
              icon={dashboard.icon}
              context={dashboard.context}
              control={context}
              onChange={handleChangeContext}
            />
          )}

          <Tooltip
            content={disabledControls?.get('period')}
            disabled={!disabledControls?.has('period')}
            className={styles.tooltip}
          >
            <AnalyticsPeriod
              period={period}
              settings={settings}
              onChangePeriod={handleChangePeriod}
              onChangeSettings={handleChangeSettings}
              buttonProps={{ disabled: disabledControls?.has('period') }}
            />
          </Tooltip>
        </FlexBox>

        {dashboard.filters.length > 0 && (
          <>
            {initiallyCollapsedFilters && (
              <Button
                variant="tertiary"
                slotStart={
                  <Icon name={hasCollapsedFilters ? 'plus' : 'minus'} />
                }
                onClick={toggleHasCollapseFilters}
                {...(filters.length > 0 && { label: filters.length })}
              >
                {hasCollapsedFilters ? 'More filters' : 'Less filters'}
              </Button>
            )}

            <AnalyticsFilters
              panel={panel}
              dashboardFilters={dashboard.filters}
              isCollapsed={hasCollapsedFilters}
              onChange={handleChangeFilters}
            />
          </>
        )}
      </div>
    </ErrorBoundary>
  );
});
